import React, { useState, useEffect } from 'react';
import { FiChevronUp, FiChevronDown, FiAlertTriangle, FiSave, FiChevronLeft, FiChevronRight, FiHome, FiCheckSquare, FiLayout, FiTool, FiUser, FiLogOut, FiClock, FiCommand, FiMapPin, FiUsers, FiLayers, FiCalendar } from 'react-icons/fi';
import { cleaningTypesAPI, workSchedulesAPI, roomGroupsAPI } from '../../services/api';
import { IoBedOutline } from 'react-icons/io5';
import { GiBroom } from 'react-icons/gi';
import clsx from 'clsx';
import LoadingSpinner from '../common/LoadingSpinner';
import { calculateModifiedCleaningTime } from '../../utils/timeCalculator';

const CleaningOrdersTable = ({
  rooms,
  commonSpaces = [],
  roomOrders,
  cleaningStaff,
  onOrderChange,
  onOrderCreate,
  onOrderDelete,
  hasChanges,
  onSave,
  isSaving,
  selectedDate,
  onDateChange,
  onPreviousDay,
  onNextDay,
  onCheckoutStatusChange,
  onCleaningStatusChange,
  establishmentId = null
}) => {
  const [cleaningTypes, setCleaningTypes] = useState([]);
  const [roomGroups, setRoomGroups] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'number', direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [showStaffSchedule, setShowStaffSchedule] = useState(false);
  const [staffSchedules, setStaffSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [savingScheduleForStaff, setSavingScheduleForStaff] = useState(null); // staff id being saved
  const [filters, setFilters] = useState({
    hasOrder: 'all', // 'all', 'with', 'without'
    assignedStaff: 'all',
    bedConfig: 'all', // 'all', 'individual', 'matrimonio', 'separadas'
    cleaningType: 'all', // 'all', or cleaning type id
    floor: 'all' // 'all', or floor number
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    itemKey: null,
    itemName: null,
    staffName: null
  });

  // Keyboard navigation state
  const [focusedCell, setFocusedCell] = useState(null); // { rowIndex, columnIndex }
  const [isEditMode, setIsEditMode] = useState(false);
  const tableRef = React.useRef(null);

  useEffect(() => {
    fetchCleaningTypes();
    fetchRoomGroups();
  }, []);

  useEffect(() => {
    if (showStaffSchedule && selectedDate) {
      fetchStaffSchedules();
    }
  }, [showStaffSchedule, selectedDate]);

  // Auto-focus on the focused cell
  useEffect(() => {
    if (focusedCell && tableRef.current) {
      const { rowIndex, columnIndex } = focusedCell;
      const cellElement = tableRef.current.querySelector(
        `[data-row="${rowIndex}"][data-col="${columnIndex}"]`
      );
      if (cellElement) {
        cellElement.focus();
      }
    }
  }, [focusedCell]);

  // Global keyboard shortcut for saving (Ctrl+S, Cmd+S, Ctrl+G)
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifierKey = isMac ? e.metaKey : e.ctrlKey;

      if (modifierKey && (e.key === 's' || e.key === 'S' || e.key === 'g' || e.key === 'G')) {
        e.preventDefault();
        if (hasChanges && !isSaving) {
          onSave();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [hasChanges, isSaving, onSave]);

  const fetchCleaningTypes = async () => {
    try {
      const params = establishmentId ? { establishment_id: establishmentId } : {};
      const types = await cleaningTypesAPI.getAll(params);
      setCleaningTypes(types);
    } catch (error) {
      console.error('Error fetching cleaning types:', error);
      // Fallback to default types
      setCleaningTypes([
        { id: 'checkout', name: 'Check-out' },
        { id: 'maintenance', name: 'Mantenimiento' }
      ]);
    }
  };

  const fetchRoomGroups = async () => {
    try {
      const params = establishmentId ? { establishment_id: establishmentId } : {};
      const groups = await roomGroupsAPI.getAll(params);
      setRoomGroups(groups);
    } catch (error) {
      console.error('Error fetching room groups:', error);
      setRoomGroups([]);
    }
  };

  const fetchStaffSchedules = async () => {
    try {
      setLoadingSchedules(true);
      const date = new Date(selectedDate);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;

      // Get schedules for the current month
      const schedules = await workSchedulesAPI.getByMonth(year, month);

      // Filter schedules for the selected date
      const dateStr = selectedDate;
      const daySchedules = schedules.filter(schedule => {
        // Compare date strings directly to avoid timezone issues
        const scheduleDateStr = schedule.date.split('T')[0];
        return scheduleDateStr === dateStr;
      });

      setStaffSchedules(daySchedules);
    } catch (error) {
      console.error('Error fetching staff schedules:', error);
      setStaffSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  };

  // Función para aplicar el cuadrante estimado basado en las órdenes asignadas
  const applyEstimatedSchedule = async (staffId, estimatedHours, existingSchedule) => {
    if (!establishmentId) {
      console.error('No establishment ID available');
      return;
    }

    setSavingScheduleForStaff(staffId);

    try {
      // Determinar el turno según las horas
      let shift = 'Parcial';
      if (estimatedHours >= 7) {
        shift = 'Completo';
      } else if (estimatedHours >= 4) {
        shift = 'Medio';
      }

      if (existingSchedule) {
        // Actualizar cuadrante existente
        await workSchedulesAPI.update(existingSchedule.id, {
          hours: parseFloat(estimatedHours.toFixed(1)),
          shift: shift,
          notes: `Cuadrante generado automáticamente desde órdenes de limpieza`
        });
      } else {
        // Crear nuevo cuadrante
        await workSchedulesAPI.create({
          establishment_id: establishmentId,
          employee_id: staffId,
          date: selectedDate,
          hours: parseFloat(estimatedHours.toFixed(1)),
          shift: shift,
          notes: `Cuadrante generado automáticamente desde órdenes de limpieza`
        });
      }

      // Refrescar los cuadrantes
      await fetchStaffSchedules();
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Error al guardar el cuadrante. Por favor, inténtelo de nuevo.');
    } finally {
      setSavingScheduleForStaff(null);
    }
  };

  // Función para extraer el número de planta del número de habitación
  const getFloorFromRoomNumber = (roomNumber) => {
    if (!roomNumber) return null;
    // Extraer los primeros dígitos del número de habitación
    // Ej: "101" -> "1", "201" -> "2", "1001" -> "10", "1A" -> "1"
    const match = roomNumber.toString().match(/^(\d+)/);
    if (match) {
      const digits = match[1];
      // Si tiene 3 dígitos o menos, el primer dígito es la planta
      // Si tiene 4 o más dígitos, los primeros 2 dígitos son la planta
      if (digits.length <= 3) {
        return digits.charAt(0);
      } else {
        return digits.substring(0, 2);
      }
    }
    return null;
  };

  // Obtener lista única de plantas de todas las habitaciones
  const availableFloors = React.useMemo(() => {
    return [...new Set(
      rooms
        .map(room => getFloorFromRoomNumber(room.number))
        .filter(floor => floor !== null)
    )].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [rooms]);

  // Sort cleaning staff alphabetically for consistent ordering
  const sortedCleaningStaff = React.useMemo(() => {
    return [...cleaningStaff].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, [cleaningStaff]);

  // Create room-order mapping from props
  const roomOrdersMap = React.useMemo(() => {
    const map = {};
    rooms.forEach(room => {
      const key = `room-${room.id}`;
      const roomOrdersList = roomOrders[key] || [];
      const mainOrder = roomOrdersList[0] || null;
      map[room.id] = {
        room,
        order: mainOrder,
        hasOrder: !!mainOrder
      };
    });
    return map;
  }, [rooms, roomOrders]);

  // Create common-space-order mapping from props
  const spaceOrdersMap = React.useMemo(() => {
    const map = {};
    commonSpaces.forEach(space => {
      const key = `space-${space.id}`;
      const spaceOrdersList = roomOrders[key] || [];
      const mainOrder = spaceOrdersList[0] || null;
      map[space.id] = {
        space,
        order: mainOrder,
        hasOrder: !!mainOrder
      };
    });
    return map;
  }, [commonSpaces, roomOrders]);

  // Filter and sort data
  const filteredAndSortedRooms = React.useMemo(() => {
    let filteredRooms = rooms.filter(room => {
      const roomData = roomOrdersMap[room.id];

      // Filter by floor
      if (filters.floor !== 'all') {
        const roomFloor = getFloorFromRoomNumber(room.number);
        if (roomFloor !== filters.floor) return false;
      }

      // Filter by order status
      if (filters.hasOrder === 'with' && !roomData.hasOrder) return false;
      if (filters.hasOrder === 'without' && roomData.hasOrder) return false;

      // Filter by assigned staff
      if (filters.assignedStaff !== 'all') {
        if (!roomData.order || roomData.order.assigned_staff_id?.toString() !== filters.assignedStaff) {
          return false;
        }
      }

      // Filter by bed disposition
      if (filters.bedConfig !== 'all') {
        if (!roomData.order || roomData.order.bed_disposition_id?.toString() !== filters.bedConfig) {
          return false;
        }
      }

      // Filter by cleaning type
      if (filters.cleaningType !== 'all') {
        if (!roomData.order || roomData.order.cleaning_type_id?.toString() !== filters.cleaningType) {
          return false;
        }
      }

      return true;
    });

    // Sort rooms alphabetically
    filteredRooms.sort((a, b) => {
      if (sortConfig.key === 'number') {
        const aVal = a.number;
        const bVal = b.number;

        // Always use alphabetical comparison
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' })
          : bVal.localeCompare(aVal, undefined, { numeric: true, sensitivity: 'base' });
      }
      return 0;
    });

    return filteredRooms;
  }, [rooms, roomOrdersMap, sortConfig, filters]);

  // Group rooms by room_group_id
  const groupedRooms = React.useMemo(() => {
    const groups = [];

    // Sort roomGroups by display_order
    const sortedGroups = [...roomGroups].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    // Add rooms for each group
    sortedGroups.forEach(group => {
      const groupRooms = filteredAndSortedRooms.filter(room => room.room_group_id === group.id);
      if (groupRooms.length > 0) {
        groups.push({
          group,
          rooms: groupRooms
        });
      }
    });

    // Add rooms without a group
    const ungroupedRooms = filteredAndSortedRooms.filter(room => !room.room_group_id);
    if (ungroupedRooms.length > 0) {
      groups.push({
        group: null, // null indicates ungrouped
        rooms: ungroupedRooms
      });
    }

    return groups;
  }, [filteredAndSortedRooms, roomGroups]);

  // Flat list of rooms in visual order (respecting groups) for keyboard navigation
  const flatRoomsList = React.useMemo(() => {
    if (roomGroups.length === 0) {
      // No groups - use the standard sorted list
      return filteredAndSortedRooms;
    }

    // Build flat list from grouped rooms in display order
    const flatList = [];
    groupedRooms.forEach(groupData => {
      flatList.push(...groupData.rooms);
    });
    return flatList;
  }, [filteredAndSortedRooms, roomGroups, groupedRooms]);

  // Filter and sort common spaces
  const filteredAndSortedSpaces = React.useMemo(() => {
    let filteredSpaces = commonSpaces.filter(space => {
      const spaceData = spaceOrdersMap[space.id];

      // Filter by order status
      if (filters.hasOrder === 'with' && !spaceData.hasOrder) return false;
      if (filters.hasOrder === 'without' && spaceData.hasOrder) return false;

      // Filter by assigned staff
      if (filters.assignedStaff !== 'all') {
        if (!spaceData.order || spaceData.order.assigned_staff_id?.toString() !== filters.assignedStaff) {
          return false;
        }
      }

      // Filter by cleaning type
      if (filters.cleaningType !== 'all') {
        if (!spaceData.order || spaceData.order.cleaning_type_id?.toString() !== filters.cleaningType) {
          return false;
        }
      }

      // Note: Bed disposition filter doesn't apply to spaces
      return true;
    });

    // Sort spaces alphabetically by name
    filteredSpaces.sort((a, b) => {
      const aVal = a.name;
      const bVal = b.name;
      return sortConfig.direction === 'asc'
        ? aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' })
        : bVal.localeCompare(aVal, undefined, { numeric: true, sensitivity: 'base' });
    });

    return filteredSpaces;
  }, [commonSpaces, spaceOrdersMap, sortConfig, filters]);

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle order checkbox change
  const handleOrderToggle = (item, hasOrder, type = 'room') => {
    const itemKey = type === 'room' ? `room-${item.id}` : `space-${item.id}`;
    const orderMap = type === 'room' ? roomOrdersMap : spaceOrdersMap;

    // If unchecking and has assigned staff, show confirmation dialog
    if (!hasOrder && orderMap[item.id]?.order?.assigned_staff_id) {
      const staffId = orderMap[item.id].order.assigned_staff_id;
      const staff = sortedCleaningStaff.find(s => s.id === staffId);

      setConfirmDialog({
        isOpen: true,
        itemKey: itemKey,
        itemName: type === 'room' ? `Habitación ${item.number}` : item.name,
        staffName: staff?.name || 'Personal asignado'
      });
      return;
    }

    // Otherwise proceed normally
    onOrderChange(itemKey, 'hasOrder', hasOrder, cleaningTypes);
  };

  // Confirm uncheck order
  const confirmUncheckOrder = () => {
    if (confirmDialog.itemKey) {
      onOrderChange(confirmDialog.itemKey, 'hasOrder', false, cleaningTypes);
    }
    setConfirmDialog({ isOpen: false, itemKey: null, itemName: null, staffName: null });
  };

  // Cancel uncheck order
  const cancelUncheckOrder = () => {
    setConfirmDialog({ isOpen: false, itemKey: null, itemName: null, staffName: null });
  };

  // Handle field changes
  const handleFieldChange = (item, field, value, type = 'room') => {
    const itemKey = type === 'room' ? `room-${item.id}` : `space-${item.id}`;
    const itemOrdersList = roomOrders[itemKey] || [];

    if (itemOrdersList.length > 0) {
      // If changing cleaning_type_id, also update the name
      if (field === 'cleaning_type_id') {
        const cleaningType = cleaningTypes.find(t => t.id === value);
        const mainOrder = itemOrdersList[0];
        if (mainOrder && cleaningType) {
          onOrderChange(itemKey, field, value);
          // Also update the name for display purposes
          mainOrder.cleaning_type_name = cleaningType.name;
        }
      } else {
        onOrderChange(itemKey, field, value);
      }
    }
  };

  // Get bed configuration options based on room's bed dispositions
  const getBedConfigOptions = (room) => {
    if (!room.bed_dispositions || room.bed_dispositions.length === 0) {
      return [];
    }

    // Return room's configured bed dispositions
    return room.bed_dispositions
      .filter(d => d.is_active)
      .sort((a, b) => a.display_order - b.display_order)
      .map(d => ({
        value: d.id,
        label: d.name
      }));
  };

  // Keyboard navigation - Define editable columns for rooms
  const EDITABLE_COLUMNS = {
    HAS_ORDER: 1,      // Toggle limpieza
    BED_CONFIG: 2,     // Tipo de camas
    CLEANING_TYPE: 3,  // Tipo de limpieza
    STAFF: 4           // Personal asignado
  };

  // Define editable columns for common spaces (no bed config or cleaning type)
  const SPACE_EDITABLE_COLUMNS = {
    HAS_ORDER: 1,      // Toggle limpieza
    STAFF: 2           // Personal asignado
  };

  // Helper to determine if a row index is in the spaces section
  const isSpaceRow = (rowIndex) => rowIndex >= flatRoomsList.length;

  // Get the space index from a combined row index
  const getSpaceIndex = (rowIndex) => rowIndex - flatRoomsList.length;

  // Navigate to specific cell
  const navigateToCell = (rowIndex, columnIndex) => {
    const totalRows = flatRoomsList.length + filteredAndSortedSpaces.length;

    // Validate row bounds
    if (rowIndex < 0 || rowIndex >= totalRows) return;

    // Validate column bounds based on whether it's a room or space row
    if (isSpaceRow(rowIndex)) {
      // Space row - only HAS_ORDER and STAFF columns
      if (columnIndex < SPACE_EDITABLE_COLUMNS.HAS_ORDER || columnIndex > SPACE_EDITABLE_COLUMNS.STAFF) return;
    } else {
      // Room row - all columns
      if (columnIndex < EDITABLE_COLUMNS.HAS_ORDER || columnIndex > EDITABLE_COLUMNS.STAFF) return;
    }

    setFocusedCell({ rowIndex, columnIndex });
    setIsEditMode(false);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, columnIndex) => {
    // Don't handle if in edit mode for dropdowns or if a dialog is open
    if (confirmDialog.isOpen) return;

    const { key, shiftKey, ctrlKey, metaKey } = e;

    // Tab navigation
    if (key === 'Tab') {
      e.preventDefault();
      const direction = shiftKey ? -1 : 1;
      navigateTab(rowIndex, columnIndex, direction);
      return;
    }

    // Arrow navigation
    if (key === 'ArrowUp') {
      e.preventDefault();
      const newRowIndex = rowIndex - 1;
      if (newRowIndex >= 0) {
        // Adjust column when transitioning between sections
        let newCol = columnIndex;
        if (isSpaceRow(rowIndex) && !isSpaceRow(newRowIndex)) {
          // Moving from space to room - map column 2 (STAFF) to column 4 (STAFF)
          if (columnIndex === SPACE_EDITABLE_COLUMNS.STAFF) {
            newCol = EDITABLE_COLUMNS.STAFF;
          }
        } else if (!isSpaceRow(rowIndex) && isSpaceRow(newRowIndex)) {
          // Moving from room to space - map column 4 (STAFF) to column 2 (STAFF)
          if (columnIndex > SPACE_EDITABLE_COLUMNS.STAFF) {
            newCol = SPACE_EDITABLE_COLUMNS.STAFF;
          }
        }
        navigateToCell(newRowIndex, newCol);
      }
      return;
    }

    if (key === 'ArrowDown') {
      e.preventDefault();
      const totalRows = flatRoomsList.length + filteredAndSortedSpaces.length;
      const newRowIndex = rowIndex + 1;
      if (newRowIndex < totalRows) {
        // Adjust column when transitioning between sections
        let newCol = columnIndex;
        if (!isSpaceRow(rowIndex) && isSpaceRow(newRowIndex)) {
          // Moving from room to space - map column 4 (STAFF) to column 2 (STAFF)
          if (columnIndex > SPACE_EDITABLE_COLUMNS.STAFF) {
            newCol = SPACE_EDITABLE_COLUMNS.STAFF;
          }
        } else if (isSpaceRow(rowIndex) && !isSpaceRow(newRowIndex)) {
          // Moving from space to room - map column 2 (STAFF) to column 4 (STAFF)
          if (columnIndex === SPACE_EDITABLE_COLUMNS.STAFF) {
            newCol = EDITABLE_COLUMNS.STAFF;
          }
        }
        navigateToCell(newRowIndex, newCol);
      }
      return;
    }

    if (key === 'ArrowLeft') {
      e.preventDefault();
      // Handle transition between sections
      const minCol = isSpaceRow(rowIndex) ? SPACE_EDITABLE_COLUMNS.HAS_ORDER : EDITABLE_COLUMNS.HAS_ORDER;
      if (columnIndex > minCol) {
        navigateToCell(rowIndex, columnIndex - 1);
      }
      return;
    }

    if (key === 'ArrowRight') {
      e.preventDefault();
      // Handle transition between sections
      const maxCol = isSpaceRow(rowIndex) ? SPACE_EDITABLE_COLUMNS.STAFF : EDITABLE_COLUMNS.STAFF;
      if (columnIndex < maxCol) {
        navigateToCell(rowIndex, columnIndex + 1);
      }
      return;
    }

    // Enter or Space to activate/toggle
    if (key === 'Enter' || key === ' ') {
      e.preventDefault();
      handleCellActivation(rowIndex, columnIndex);
      return;
    }

    // Escape to exit edit mode
    if (key === 'Escape') {
      e.preventDefault();
      setIsEditMode(false);
      return;
    }

    // Number keys for quick selection (0-9)
    if (!isEditMode) {
      const numKey = parseInt(key);
      if (!isNaN(numKey) && numKey >= 0 && numKey <= 9) {
        e.preventDefault();
        handleQuickSelection(rowIndex, columnIndex, numKey);
        return;
      }
    }
  };

  // Tab navigation - move to next/previous editable field
  const navigateTab = (currentRow, currentCol, direction) => {
    const totalRows = flatRoomsList.length + filteredAndSortedSpaces.length;
    let newRow = currentRow;
    let newCol = currentCol + direction;

    // Determine max column based on current row type
    const isCurrentSpace = isSpaceRow(currentRow);
    const maxCol = isCurrentSpace ? SPACE_EDITABLE_COLUMNS.STAFF : EDITABLE_COLUMNS.STAFF;
    const minCol = isCurrentSpace ? SPACE_EDITABLE_COLUMNS.HAS_ORDER : EDITABLE_COLUMNS.HAS_ORDER;

    // Wrap to next/previous row
    if (newCol > maxCol) {
      newRow++;
      // When moving to next row, check if it's a space row
      if (newRow < totalRows) {
        newCol = isSpaceRow(newRow) ? SPACE_EDITABLE_COLUMNS.HAS_ORDER : EDITABLE_COLUMNS.HAS_ORDER;
      }
    } else if (newCol < minCol) {
      newRow--;
      // When moving to previous row, check if it's a space row
      if (newRow >= 0) {
        newCol = isSpaceRow(newRow) ? SPACE_EDITABLE_COLUMNS.STAFF : EDITABLE_COLUMNS.STAFF;
      }
    }

    // Check bounds
    if (newRow >= 0 && newRow < totalRows) {
      navigateToCell(newRow, newCol);
    }
  };

  // Handle cell activation (Enter or Space)
  const handleCellActivation = (rowIndex, columnIndex) => {
    // Check if we're in the spaces section
    if (isSpaceRow(rowIndex)) {
      const spaceIndex = getSpaceIndex(rowIndex);
      const space = filteredAndSortedSpaces[spaceIndex];
      const spaceData = spaceOrdersMap[space.id];

      switch (columnIndex) {
        case SPACE_EDITABLE_COLUMNS.HAS_ORDER:
          // Toggle order
          handleOrderToggle(space, !spaceData.hasOrder, 'space');
          break;

        case SPACE_EDITABLE_COLUMNS.STAFF:
          // Cycle through staff options
          if (spaceData.hasOrder) {
            const currentValue = spaceData.order?.assigned_staff_id;
            // Build array with null (unassigned) + all staff
            const staffOptions = [null, ...sortedCleaningStaff.map(s => s.id)];
            const currentIndex = staffOptions.findIndex(id => id === currentValue);
            const nextIndex = (currentIndex + 1) % staffOptions.length;
            handleFieldChange(space, 'assigned_staff_id', staffOptions[nextIndex], 'space');
          }
          break;
      }
      return;
    }

    // Room row handling
    const room = flatRoomsList[rowIndex];
    const roomData = roomOrdersMap[room.id];

    switch (columnIndex) {
      case EDITABLE_COLUMNS.HAS_ORDER:
        // Toggle order
        handleOrderToggle(room, !roomData.hasOrder);
        break;

      case EDITABLE_COLUMNS.BED_CONFIG:
      case EDITABLE_COLUMNS.CLEANING_TYPE:
        // For multi-button columns, enter edit mode or toggle if only one option
        if (roomData.hasOrder) {
          if (columnIndex === EDITABLE_COLUMNS.BED_CONFIG) {
            const bedOptions = getBedConfigOptions(room);
            if (bedOptions.length === 1) {
              handleFieldChange(room, 'bed_disposition_id', bedOptions[0].value);
            } else if (bedOptions.length > 1) {
              // Cycle through options
              const currentValue = roomData.order?.bed_disposition_id;
              const currentIndex = bedOptions.findIndex(opt => opt.value === currentValue);
              const nextIndex = (currentIndex + 1) % bedOptions.length;
              handleFieldChange(room, 'bed_disposition_id', bedOptions[nextIndex].value);
            }
          } else {
            // Cycle through cleaning types
            const currentValue = roomData.order?.cleaning_type_id;
            const currentIndex = cleaningTypes.findIndex(t => t.id === currentValue);
            const nextIndex = (currentIndex + 1) % cleaningTypes.length;
            handleFieldChange(room, 'cleaning_type_id', cleaningTypes[nextIndex].id);
          }
        }
        break;

      case EDITABLE_COLUMNS.STAFF:
        // Cycle through staff options
        if (roomData.hasOrder) {
          const currentValue = roomData.order?.assigned_staff_id;
          // Build array with null (unassigned) + all staff
          const staffOptions = [null, ...sortedCleaningStaff.map(s => s.id)];
          const currentIndex = staffOptions.findIndex(id => id === currentValue);
          const nextIndex = (currentIndex + 1) % staffOptions.length;
          handleFieldChange(room, 'assigned_staff_id', staffOptions[nextIndex]);
        }
        break;
    }
  };

  // Quick selection with number keys
  const handleQuickSelection = (rowIndex, columnIndex, optionIndex) => {
    // Check if we're in the spaces section
    if (isSpaceRow(rowIndex)) {
      const spaceIndex = getSpaceIndex(rowIndex);
      const space = filteredAndSortedSpaces[spaceIndex];
      const spaceData = spaceOrdersMap[space.id];

      if (!spaceData.hasOrder) return;

      if (columnIndex === SPACE_EDITABLE_COLUMNS.STAFF) {
        // For staff: 0 = unassigned, 1-9 = staff members
        if (optionIndex === 0) {
          handleFieldChange(space, 'assigned_staff_id', null, 'space');
        } else if (optionIndex - 1 < sortedCleaningStaff.length) {
          handleFieldChange(space, 'assigned_staff_id', sortedCleaningStaff[optionIndex - 1].id, 'space');
        }
      }
      return;
    }

    // Room row handling
    const room = flatRoomsList[rowIndex];
    const roomData = roomOrdersMap[room.id];

    if (!roomData.hasOrder) return;

    if (columnIndex === EDITABLE_COLUMNS.BED_CONFIG) {
      const bedOptions = getBedConfigOptions(room);
      if (optionIndex < bedOptions.length) {
        handleFieldChange(room, 'bed_disposition_id', bedOptions[optionIndex].value);
      }
    } else if (columnIndex === EDITABLE_COLUMNS.CLEANING_TYPE) {
      if (optionIndex < cleaningTypes.length) {
        handleFieldChange(room, 'cleaning_type_id', cleaningTypes[optionIndex].id);
      }
    } else if (columnIndex === EDITABLE_COLUMNS.STAFF) {
      // For staff: 0 = unassigned, 1-9 = staff members
      if (optionIndex === 0) {
        handleFieldChange(room, 'assigned_staff_id', null);
      } else if (optionIndex - 1 < sortedCleaningStaff.length) {
        handleFieldChange(room, 'assigned_staff_id', sortedCleaningStaff[optionIndex - 1].id);
      }
    }
  };

  // Click handler to set focus
  const handleCellClick = (rowIndex, columnIndex) => {
    setFocusedCell({ rowIndex, columnIndex });
    setIsEditMode(false);
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header with title, date, and save button */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative flex items-center justify-center mb-4">
          {/* Left side - Filter and Keyboard Help buttons */}
          <div className="absolute left-0 flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              aria-label={showFilters ? "Ocultar filtros" : "Mostrar filtros"}
            >
              {showFilters ? (
                <>
                  <FiChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>Ocultar filtros</span>
                </>
              ) : (
                <>
                  <FiChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>Mostrar filtros</span>
                </>
              )}
            </button>

            <button
              onClick={() => setShowKeyboardHelp(!showKeyboardHelp)}
              className="flex items-center space-x-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              aria-label="Mostrar atajos de teclado"
              title="Atajos de teclado"
            >
              <FiCommand className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Teclado</span>
            </button>

            <button
              onClick={() => setShowStaffSchedule(!showStaffSchedule)}
              className={clsx(
                'flex items-center space-x-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors',
                {
                  'text-white bg-blue-600 hover:bg-blue-700': showStaffSchedule,
                  'text-blue-700 bg-blue-50 hover:bg-blue-100': !showStaffSchedule
                }
              )}
              aria-label="Ver personal y cuadrantes"
              title="Ver personal y cuadrantes"
            >
              <FiUsers className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Personal</span>
            </button>
          </div>

          {/* Center - Date selector with navigation */}
          <div className="flex items-center space-x-2">
            {/* Today button - only show if not viewing today */}
            {selectedDate !== new Date().toISOString().split('T')[0] && (
              <button
                onClick={() => onDateChange(new Date().toISOString().split('T')[0])}
                className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                title="Ver día de hoy"
              >
                Hoy
              </button>
            )}

            {/* Previous day button */}
            <button
              onClick={onPreviousDay}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              aria-label="Día anterior"
              title="Día anterior"
            >
              <FiChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>

            {/* Date input */}
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => onDateChange(e.target.value)}
              className="form-input min-w-0"
              title="Selecciona la fecha para ver las órdenes de limpieza de ese día"
            />

            {/* Next day button */}
            <button
              onClick={onNextDay}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              aria-label="Día siguiente"
              title="Día siguiente"
            >
              <FiChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {/* Right side - Floor filter and Changes indicator */}
          <div className="absolute right-0 flex items-center space-x-3">
            {/* Filtro por planta - Siempre visible */}
            {availableFloors.length > 0 && (
              <div className="flex items-center space-x-1.5">
                <FiLayout className="h-4 w-4 text-purple-600 flex-shrink-0" aria-hidden="true" />
                <select
                  value={filters.floor}
                  onChange={(e) => setFilters(prev => ({ ...prev, floor: e.target.value }))}
                  className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 shadow-sm bg-white"
                  title="Filtrar por planta"
                >
                  <option value="all">Todas las plantas</option>
                  {availableFloors.map(floor => (
                    <option key={floor} value={floor}>
                      Planta {floor}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {hasChanges && (
              <span className="text-sm text-yellow-600 font-medium">
                Cambios sin guardar
              </span>
            )}
          </div>
        </div>

        {/* Keyboard shortcuts help */}
        {showKeyboardHelp && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-xs font-bold text-blue-900 mb-2">Atajos de teclado disponibles:</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
              <div className="flex items-start space-x-2">
                <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono text-[10px]">Tab</kbd>
                <span className="text-gray-700">Siguiente campo</span>
              </div>
              <div className="flex items-start space-x-2">
                <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono text-[10px]">Shift+Tab</kbd>
                <span className="text-gray-700">Campo anterior</span>
              </div>
              <div className="flex items-start space-x-2">
                <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono text-[10px]">↑↓←→</kbd>
                <span className="text-gray-700">Navegar por la tabla</span>
              </div>
              <div className="flex items-start space-x-2">
                <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono text-[10px]">Enter</kbd>
                <span className="text-gray-700">Activar/Alternar opción</span>
              </div>
              <div className="flex items-start space-x-2">
                <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono text-[10px]">Espacio</kbd>
                <span className="text-gray-700">Activar/Alternar opción</span>
              </div>
              <div className="flex items-start space-x-2">
                <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono text-[10px]">0-9</kbd>
                <span className="text-gray-700">Selección rápida (0=Sin asignar en Personal)</span>
              </div>
              <div className="flex items-start space-x-2">
                <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono text-[10px]">Esc</kbd>
                <span className="text-gray-700">Salir del campo</span>
              </div>
              <div className="flex items-start space-x-2">
                <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono text-[10px]">Ctrl+S</kbd>
                <span className="text-gray-700">Guardar cambios (Cmd+S en Mac)</span>
              </div>
            </div>
            <p className="mt-2 text-[10px] text-blue-800 italic">
              💡 Tip: Haz clic en cualquier celda editable (Limpieza, Disposición, Tipo, Personal) para comenzar a navegar con el teclado
            </p>
          </div>
        )}

        {/* Filters row */}
        {showFilters && (
          <div className="flex items-center space-x-3 flex-wrap gap-y-2">
          {/* Solo con órdenes de limpieza - Switch */}
          <div className="flex items-center space-x-1.5">
            <button
              onClick={() => setFilters(prev => ({ ...prev, hasOrder: prev.hasOrder === 'with' ? 'all' : 'with' }))}
              className={clsx(
                'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 shadow-sm',
                {
                  'bg-blue-500 focus:ring-blue-500': filters.hasOrder === 'with',
                  'bg-gray-300 focus:ring-gray-400': filters.hasOrder !== 'with'
                }
              )}
            >
              <span
                className={clsx(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-md',
                  {
                    'translate-x-4': filters.hasOrder === 'with',
                    'translate-x-0.5': filters.hasOrder !== 'with'
                  }
                )}
              />
            </button>
            <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
              Solo con órdenes
            </span>
          </div>

          {/* Divisor vertical */}
          <div className="h-6 w-px bg-gray-300"></div>

          {/* Disposición de camas - Icono + Chips */}
          <div className="flex items-center space-x-1.5">
            <IoBedOutline className="h-4 w-4 text-blue-600 flex-shrink-0" aria-hidden="true" />
            <div className="flex items-center space-x-1 flex-wrap gap-0.5">
              <button
                onClick={() => setFilters(prev => ({ ...prev, bedConfig: 'all' }))}
                className={clsx(
                  'px-2 py-0.5 text-xs font-medium rounded-full transition-all',
                  {
                    'bg-blue-500 text-white shadow-sm': filters.bedConfig === 'all',
                    'bg-gray-200 text-gray-700 hover:bg-gray-300': filters.bedConfig !== 'all'
                  }
                )}
              >
                Todas
              </button>
              {/* Get unique bed dispositions from all rooms */}
              {Array.from(new Set(
                rooms.flatMap(room =>
                  room.bed_dispositions?.filter(d => d.is_active).map(d => JSON.stringify({ id: d.id, name: d.name })) || []
                )
              )).map(dispJson => {
                const disposition = JSON.parse(dispJson);
                return (
                  <button
                    key={disposition.id}
                    onClick={() => setFilters(prev => ({ ...prev, bedConfig: disposition.id.toString() }))}
                    className={clsx(
                      'px-2 py-0.5 text-xs font-medium rounded-full transition-all',
                      {
                        'bg-blue-500 text-white shadow-sm': filters.bedConfig === disposition.id.toString(),
                        'bg-gray-200 text-gray-700 hover:bg-gray-300': filters.bedConfig !== disposition.id.toString()
                      }
                    )}
                  >
                    {disposition.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Divisor vertical */}
          <div className="h-6 w-px bg-gray-300"></div>

          {/* Tipo de limpieza - Icono + Chips */}
          <div className="flex items-center space-x-1.5">
            <GiBroom className="h-4 w-4 text-green-500 flex-shrink-0" aria-hidden="true" />
            <div className="flex items-center space-x-1 flex-wrap gap-0.5">
              <button
                onClick={() => setFilters(prev => ({ ...prev, cleaningType: 'all' }))}
                className={clsx(
                  'px-2 py-0.5 text-xs font-medium rounded-full transition-all',
                  {
                    'bg-green-500 text-white shadow-sm': filters.cleaningType === 'all',
                    'bg-gray-200 text-gray-700 hover:bg-gray-300': filters.cleaningType !== 'all'
                  }
                )}
              >
                Todos
              </button>
              {cleaningTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => setFilters(prev => ({ ...prev, cleaningType: type.id.toString() }))}
                  className={clsx(
                    'px-2 py-0.5 text-xs font-medium rounded-full transition-all',
                    {
                      'bg-green-500 text-white shadow-sm': filters.cleaningType === type.id.toString(),
                      'bg-gray-200 text-gray-700 hover:bg-gray-300': filters.cleaningType !== type.id.toString()
                    }
                  )}
                >
                  {type.name}
                </button>
              ))}
            </div>
          </div>

          {/* Divisor vertical */}
          <div className="h-6 w-px bg-gray-300"></div>

          {/* Personal asignado - Icono + Dropdown */}
          <div className="flex items-center space-x-1.5">
            <FiUser className="h-4 w-4 text-blue-600 flex-shrink-0" aria-hidden="true" />
            <select
              value={filters.assignedStaff}
              onChange={(e) => setFilters(prev => ({ ...prev, assignedStaff: e.target.value }))}
              className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
            >
              <option value="all">Todo el personal</option>
              {sortedCleaningStaff.map(staff => (
                <option key={staff.id} value={staff.id}>
                  {staff.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        )}
      </div>

      {/* Main content area with table and side panel */}
      <div className="flex gap-4">
        {/* Table */}
        <div className={clsx(
          'transition-all duration-300',
          {
            'w-full': !showStaffSchedule,
            'w-5/6': showStaffSchedule
          }
        )}>
          <div className="overflow-x-auto" ref={tableRef}>
        <table className="w-full">
          <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200">
            <tr>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-800">
                <div className="flex items-center space-x-1.5">
                  <FiHome className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />
                  <span>Habitación</span>
                  <button
                    onClick={() => handleSort('number')}
                    className="p-0.5 hover:bg-blue-100 rounded transition-colors"
                    aria-label="Ordenar por número de habitación"
                  >
                    {sortConfig.key === 'number' ? (
                      sortConfig.direction === 'asc' ? (
                        <FiChevronUp className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />
                      ) : (
                        <FiChevronDown className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />
                      )
                    ) : (
                      <FiChevronUp className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </th>
              <th className="px-2 py-1 text-center text-sm font-semibold text-gray-800">
                <div className="flex items-center justify-center space-x-1">
                  <FiCheckSquare className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />
                  <span>Limpieza</span>
                </div>
              </th>
              <th className="px-2 py-1 text-center text-sm font-semibold text-gray-800">
                <div className="flex items-center justify-center space-x-1">
                  <IoBedOutline className="h-4 w-4 text-blue-600" aria-hidden="true" />
                  <span>Disposición</span>
                </div>
              </th>
              <th className="px-2 py-1 text-center text-sm font-semibold text-gray-800">
                <div className="flex items-center justify-center space-x-1">
                  <GiBroom className="h-4 w-4 text-green-500 flex-shrink-0" aria-hidden="true" />
                  <span>Tipo</span>
                </div>
              </th>
              <th className="px-2 py-1 text-center text-sm font-semibold text-gray-800">
                <div className="flex items-center justify-center space-x-1">
                  <FiUser className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />
                  <span>Personal</span>
                </div>
              </th>
              <th className="px-2 py-1 text-center text-sm font-semibold text-gray-800">
                <div className="flex items-center justify-center space-x-1">
                  <FiLogOut className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />
                  <span>Check-out</span>
                </div>
              </th>
              <th className="px-2 py-1 text-center text-sm font-semibold text-gray-800">
                <div className="flex items-center justify-center space-x-1">
                  <FiClock className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />
                  <span>Estado</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Render grouped rooms if there are groups, otherwise render flat list */}
            {roomGroups.length > 0 ? (
              groupedRooms.map((groupData, groupIndex) => {
                // Calculate the starting row index for this group
                let startingRowIndex = 0;
                for (let i = 0; i < groupIndex; i++) {
                  startingRowIndex += groupedRooms[i].rooms.length;
                }

                return (
                  <React.Fragment key={groupData.group?.id || 'ungrouped'}>
                    {/* Group Header Row */}
                    <tr
                      className="border-y"
                      style={groupData.group?.color ? {
                        backgroundColor: `${groupData.group.color}20`,
                        borderColor: `${groupData.group.color}40`
                      } : {
                        background: 'linear-gradient(to right, rgb(250 245 255), rgb(238 242 255))',
                        borderColor: 'rgb(221 214 254)'
                      }}
                    >
                      <td colSpan={7} className="px-4 py-2">
                        <div className="flex items-center space-x-2">
                          {groupData.group?.color ? (
                            <span
                              className="w-4 h-4 rounded-full border border-white shadow-sm"
                              style={{ backgroundColor: groupData.group.color }}
                              aria-hidden="true"
                            />
                          ) : (
                            <FiLayers className="h-4 w-4 text-purple-600" aria-hidden="true" />
                          )}
                          <span
                            className="text-sm font-semibold"
                            style={{ color: groupData.group?.color || 'rgb(107 33 168)' }}
                          >
                            {groupData.group?.name || 'Sin grupo asignado'}
                          </span>
                          <span
                            className="text-xs px-2 py-0.5 rounded-full"
                            style={groupData.group?.color ? {
                              backgroundColor: `${groupData.group.color}30`,
                              color: groupData.group.color
                            } : {
                              backgroundColor: 'rgb(243 232 255)',
                              color: 'rgb(147 51 234)'
                            }}
                          >
                            {groupData.rooms.length} {groupData.rooms.length === 1 ? 'habitación' : 'habitaciones'}
                          </span>
                        </div>
                      </td>
                    </tr>

                    {/* Rooms in this group */}
                    {groupData.rooms.map((room, indexInGroup) => {
                      const rowIndex = startingRowIndex + indexInGroup;
                      const roomData = roomOrdersMap[room.id];
                      const bedConfigOptions = getBedConfigOptions(room);

                      return (
                        <tr key={room.id} className="hover:bg-blue-50 border-b border-gray-200 transition-colors">
                          {/* Room Number */}
                          <td className="px-3 py-1 bg-white">
                            <div className="flex items-center space-x-1.5">
                              <div className="flex items-center justify-start text-base font-bold">
                                {room.number}
                              </div>
                              {room.cleaning_time_minutes && roomData.hasOrder && (
                                <span className="text-xs text-gray-400 font-normal flex items-center gap-0.5" title="Tiempo de limpieza">
                                  <FiClock className="h-3 w-3" />
                                  {(() => {
                                    const cleaningType = roomData.order?.cleaning_type_id 
                                      ? cleaningTypes.find(t => t.id === roomData.order.cleaning_type_id)
                                      : null;
                                    const time = calculateModifiedCleaningTime(
                                      room.cleaning_time_minutes,
                                      cleaningType?.time_modifier_type || 'none',
                                      cleaningType?.time_modifier_value || 0
                                    );
                                    return `${time} min`;
                                  })()}
                                </span>
                              )}
                              {room.has_incidents_today && (
                                <FiAlertTriangle className="h-4 w-4 text-amber-500" title="Tiene incidencias hoy" aria-label="Tiene incidencias hoy" />
                              )}
                            </div>
                          </td>

                          {/* Has Order Checkbox */}
                          <td
                            className={clsx(
                              'px-2 py-1 text-center bg-white transition-all',
                              {
                                'ring-4 ring-blue-400 ring-inset': focusedCell?.rowIndex === rowIndex && focusedCell?.columnIndex === EDITABLE_COLUMNS.HAS_ORDER
                              }
                            )}
                            data-row={rowIndex}
                            data-col={EDITABLE_COLUMNS.HAS_ORDER}
                            tabIndex={0}
                            onKeyDown={(e) => handleKeyDown(e, rowIndex, EDITABLE_COLUMNS.HAS_ORDER)}
                            onClick={() => handleCellClick(rowIndex, EDITABLE_COLUMNS.HAS_ORDER)}
                          >
                            <div className="flex justify-center">
                              <button
                                onClick={() => handleOrderToggle(room, !roomData.hasOrder)}
                                tabIndex={-1}
                                className={clsx(
                                  'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 shadow-sm',
                                  {
                                    'bg-blue-500 focus:ring-blue-500': roomData.hasOrder,
                                    'bg-gray-300 focus:ring-gray-400': !roomData.hasOrder
                                  }
                                )}
                              >
                                <span
                                  className={clsx(
                                    'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-md',
                                    {
                                      'translate-x-4': roomData.hasOrder,
                                      'translate-x-0.5': !roomData.hasOrder
                                    }
                                  )}
                                />
                              </button>
                            </div>
                          </td>

                          {/* Bed Disposition Buttons */}
                          <td
                            className={clsx(
                              'px-1 py-1 bg-white transition-all',
                              {
                                'ring-4 ring-blue-400 ring-inset': focusedCell?.rowIndex === rowIndex && focusedCell?.columnIndex === EDITABLE_COLUMNS.BED_CONFIG
                              }
                            )}
                            data-row={rowIndex}
                            data-col={EDITABLE_COLUMNS.BED_CONFIG}
                            tabIndex={0}
                            onKeyDown={(e) => handleKeyDown(e, rowIndex, EDITABLE_COLUMNS.BED_CONFIG)}
                            onClick={() => handleCellClick(rowIndex, EDITABLE_COLUMNS.BED_CONFIG)}
                          >
                            <div className="flex justify-center space-x-0.5 flex-wrap gap-0.5">
                              {bedConfigOptions.length > 0 ? (
                                bedConfigOptions.map((option, index) => (
                                  <button
                                    key={option.value}
                                    onClick={() => handleFieldChange(room, 'bed_disposition_id', option.value)}
                                    disabled={!roomData.hasOrder}
                                    tabIndex={-1}
                                    title={`Opción ${index + 1}: ${option.label} (Presiona ${index + 1})`}
                                    className={clsx(
                                      'min-w-[70px] px-1.5 py-0.5 text-xs font-medium rounded transition-all',
                                      {
                                        'bg-blue-500 text-white hover:bg-blue-600': roomData.hasOrder && roomData.order?.bed_disposition_id === option.value,
                                        'bg-gray-200 text-gray-700 hover:bg-gray-300': roomData.hasOrder && roomData.order?.bed_disposition_id !== option.value,
                                        'bg-gray-100 text-gray-400 cursor-not-allowed': !roomData.hasOrder
                                      }
                                    )}
                                  >
                                    {option.label}
                                  </button>
                                ))
                              ) : (
                                <span className="text-xs text-gray-400 italic">No configuradas</span>
                              )}
                            </div>
                          </td>

                          {/* Cleaning Type Buttons */}
                          <td
                            className={clsx(
                              'px-1 py-1 bg-white transition-all',
                              {
                                'ring-4 ring-blue-400 ring-inset': focusedCell?.rowIndex === rowIndex && focusedCell?.columnIndex === EDITABLE_COLUMNS.CLEANING_TYPE
                              }
                            )}
                            data-row={rowIndex}
                            data-col={EDITABLE_COLUMNS.CLEANING_TYPE}
                            tabIndex={0}
                            onKeyDown={(e) => handleKeyDown(e, rowIndex, EDITABLE_COLUMNS.CLEANING_TYPE)}
                            onClick={() => handleCellClick(rowIndex, EDITABLE_COLUMNS.CLEANING_TYPE)}
                          >
                            <div className="flex justify-center space-x-0.5 flex-wrap gap-0.5">
                              {cleaningTypes.map((type, index) => (
                                <button
                                  key={type.id}
                                  onClick={() => handleFieldChange(room, 'cleaning_type_id', type.id)}
                                  disabled={!roomData.hasOrder}
                                  tabIndex={-1}
                                  title={`Opción ${index + 1}: ${type.name} (Presiona ${index + 1})`}
                                  className={clsx(
                                    'min-w-[75px] px-1.5 py-0.5 text-xs font-medium rounded transition-all',
                                    {
                                      'bg-green-500 text-white hover:bg-green-600': roomData.hasOrder && roomData.order?.cleaning_type_id === type.id,
                                      'bg-gray-200 text-gray-700 hover:bg-gray-300': roomData.hasOrder && roomData.order?.cleaning_type_id !== type.id,
                                      'bg-gray-100 text-gray-400 cursor-not-allowed': !roomData.hasOrder
                                    }
                                  )}
                                >
                                  {type.name}
                                </button>
                              ))}
                            </div>
                          </td>

                          {/* Assigned Staff Dropdown */}
                          <td
                            className={clsx(
                              'px-2 py-1 bg-white transition-all',
                              {
                                'ring-4 ring-blue-400 ring-inset': focusedCell?.rowIndex === rowIndex && focusedCell?.columnIndex === EDITABLE_COLUMNS.STAFF
                              }
                            )}
                            data-row={rowIndex}
                            data-col={EDITABLE_COLUMNS.STAFF}
                            tabIndex={0}
                            onKeyDown={(e) => handleKeyDown(e, rowIndex, EDITABLE_COLUMNS.STAFF)}
                            onClick={(e) => {
                              if (e.target.tagName !== 'SELECT') {
                                handleCellClick(rowIndex, EDITABLE_COLUMNS.STAFF);
                              }
                            }}
                          >
                            <select
                              value={roomData.order?.assigned_staff_id || ''}
                              onChange={(e) => handleFieldChange(room, 'assigned_staff_id', e.target.value || null)}
                              disabled={!roomData.hasOrder}
                              tabIndex={-1}
                              title={roomData.hasOrder ? 'Click para abrir o usa Enter/Espacio para ciclar (0-9 para selección rápida)' : ''}
                              className={clsx(
                                'w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 transition-all font-medium',
                                {
                                  'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200': !roomData.hasOrder,
                                  'bg-white border-red-500 focus:ring-red-500 focus:border-red-600': roomData.hasOrder && !roomData.order?.assigned_staff_id,
                                  'bg-white border-gray-300 hover:border-blue-400 focus:ring-blue-500': roomData.hasOrder && roomData.order?.assigned_staff_id
                                }
                              )}
                            >
                              <option value="">Sin asignar</option>
                              {sortedCleaningStaff.map((staff, index) => (
                                <option key={staff.id} value={staff.id}>
                                  {staff.name} ({index + 1})
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* Checkout Status Toggle */}
                          <td className="px-2 py-1 text-center bg-white">
                            <div className="flex justify-center">
                              <button
                                onClick={() => {
                                  if (roomData.hasOrder && roomData.order?.id && !roomData.order?.is_temp) {
                                    onCheckoutStatusChange(`room-${room.id}`, roomData.order.id, !roomData.order?.checkout_status);
                                  }
                                }}
                                disabled={!roomData.hasOrder || !roomData.order?.id || roomData.order?.is_temp}
                                className={clsx(
                                  'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 shadow-sm',
                                  {
                                    'bg-blue-500 focus:ring-blue-500': roomData.order?.checkout_status && roomData.hasOrder && roomData.order?.id && !roomData.order?.is_temp,
                                    'bg-gray-300 focus:ring-gray-400': !roomData.order?.checkout_status && roomData.hasOrder && roomData.order?.id && !roomData.order?.is_temp,
                                    'bg-gray-200 cursor-not-allowed': !roomData.hasOrder || !roomData.order?.id || roomData.order?.is_temp
                                  }
                                )}
                                title={!roomData.order?.id || roomData.order?.is_temp ? 'Guarda la orden primero' : roomData.order?.checkout_status ? 'Checkout realizado' : 'Checkout pendiente'}
                              >
                                <span
                                  className={clsx(
                                    'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-md',
                                    {
                                      'translate-x-4': roomData.order?.checkout_status,
                                      'translate-x-0.5': !roomData.order?.checkout_status
                                    }
                                  )}
                                />
                              </button>
                            </div>
                          </td>

                          {/* Cleaning Status */}
                          <td className="px-2 py-1 text-center bg-white">
                            <span className={clsx(
                              'inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full',
                              {
                                'bg-green-100 text-green-700 border border-green-200': roomData.order?.cleaning_complete,
                                'bg-amber-100 text-amber-700 border border-amber-200': roomData.hasOrder && !roomData.order?.cleaning_complete,
                                'bg-gray-100 text-gray-500 border border-gray-200': !roomData.hasOrder
                              }
                            )}>
                              {roomData.order?.cleaning_complete ? 'Completada' : roomData.hasOrder ? 'Pendiente' : 'Sin orden'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })
            ) : (
              // No groups - render flat list
              filteredAndSortedRooms.map((room, rowIndex) => {
                const roomData = roomOrdersMap[room.id];
                const bedConfigOptions = getBedConfigOptions(room);

                return (
                  <tr key={room.id} className="hover:bg-blue-50 border-b border-gray-200 transition-colors">
                    {/* Room Number */}
                    <td className="px-3 py-1 bg-white">
                      <div className="flex items-center space-x-1.5">
                        <div className="flex items-center justify-start text-base font-bold">
                          {room.number}
                        </div>
                        {room.cleaning_time_minutes && roomData.hasOrder && (
                          <span className="text-xs text-gray-400 font-normal flex items-center gap-0.5" title="Tiempo de limpieza">
                            <FiClock className="h-3 w-3" />
                            {(() => {
                              const cleaningType = roomData.order?.cleaning_type_id 
                                ? cleaningTypes.find(t => t.id === roomData.order.cleaning_type_id)
                                : null;
                              const time = calculateModifiedCleaningTime(
                                room.cleaning_time_minutes,
                                cleaningType?.time_modifier_type || 'none',
                                cleaningType?.time_modifier_value || 0
                              );
                              return `${time} min`;
                            })()}
                          </span>
                        )}
                        {room.has_incidents_today && (
                          <FiAlertTriangle className="h-4 w-4 text-amber-500" title="Tiene incidencias hoy" aria-label="Tiene incidencias hoy" />
                        )}
                      </div>
                    </td>

                  {/* Has Order Checkbox */}
                  <td
                    className={clsx(
                      'px-2 py-1 text-center bg-white transition-all',
                      {
                        'ring-4 ring-blue-400 ring-inset': focusedCell?.rowIndex === rowIndex && focusedCell?.columnIndex === EDITABLE_COLUMNS.HAS_ORDER
                      }
                    )}
                    data-row={rowIndex}
                    data-col={EDITABLE_COLUMNS.HAS_ORDER}
                    tabIndex={0}
                    onKeyDown={(e) => handleKeyDown(e, rowIndex, EDITABLE_COLUMNS.HAS_ORDER)}
                    onClick={() => handleCellClick(rowIndex, EDITABLE_COLUMNS.HAS_ORDER)}
                  >
                    <div className="flex justify-center">
                      <button
                        onClick={() => handleOrderToggle(room, !roomData.hasOrder)}
                        tabIndex={-1}
                        className={clsx(
                          'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 shadow-sm',
                          {
                            'bg-blue-500 focus:ring-blue-500': roomData.hasOrder,
                            'bg-gray-300 focus:ring-gray-400': !roomData.hasOrder
                          }
                        )}
                      >
                        <span
                          className={clsx(
                            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-md',
                            {
                              'translate-x-4': roomData.hasOrder,
                              'translate-x-0.5': !roomData.hasOrder
                            }
                          )}
                        />
                      </button>
                    </div>
                  </td>

                  {/* Bed Disposition Buttons */}
                  <td
                    className={clsx(
                      'px-1 py-1 bg-white transition-all',
                      {
                        'ring-4 ring-blue-400 ring-inset': focusedCell?.rowIndex === rowIndex && focusedCell?.columnIndex === EDITABLE_COLUMNS.BED_CONFIG
                      }
                    )}
                    data-row={rowIndex}
                    data-col={EDITABLE_COLUMNS.BED_CONFIG}
                    tabIndex={0}
                    onKeyDown={(e) => handleKeyDown(e, rowIndex, EDITABLE_COLUMNS.BED_CONFIG)}
                    onClick={() => handleCellClick(rowIndex, EDITABLE_COLUMNS.BED_CONFIG)}
                  >
                    <div className="flex justify-center space-x-0.5 flex-wrap gap-0.5">
                      {bedConfigOptions.length > 0 ? (
                        bedConfigOptions.map((option, index) => (
                          <button
                            key={option.value}
                            onClick={() => handleFieldChange(room, 'bed_disposition_id', option.value)}
                            disabled={!roomData.hasOrder}
                            tabIndex={-1}
                            title={`Opción ${index + 1}: ${option.label} (Presiona ${index + 1})`}
                            className={clsx(
                              'min-w-[70px] px-1.5 py-0.5 text-xs font-medium rounded transition-all',
                              {
                                'bg-blue-500 text-white hover:bg-blue-600': roomData.hasOrder && roomData.order?.bed_disposition_id === option.value,
                                'bg-gray-200 text-gray-700 hover:bg-gray-300': roomData.hasOrder && roomData.order?.bed_disposition_id !== option.value,
                                'bg-gray-100 text-gray-400 cursor-not-allowed': !roomData.hasOrder
                              }
                            )}
                          >
                            {option.label}
                          </button>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400 italic">No configuradas</span>
                      )}
                    </div>
                  </td>

                  {/* Cleaning Type Buttons */}
                  <td
                    className={clsx(
                      'px-1 py-1 bg-white transition-all',
                      {
                        'ring-4 ring-blue-400 ring-inset': focusedCell?.rowIndex === rowIndex && focusedCell?.columnIndex === EDITABLE_COLUMNS.CLEANING_TYPE
                      }
                    )}
                    data-row={rowIndex}
                    data-col={EDITABLE_COLUMNS.CLEANING_TYPE}
                    tabIndex={0}
                    onKeyDown={(e) => handleKeyDown(e, rowIndex, EDITABLE_COLUMNS.CLEANING_TYPE)}
                    onClick={() => handleCellClick(rowIndex, EDITABLE_COLUMNS.CLEANING_TYPE)}
                  >
                    <div className="flex justify-center space-x-0.5 flex-wrap gap-0.5">
                      {cleaningTypes.map((type, index) => (
                        <button
                          key={type.id}
                          onClick={() => handleFieldChange(room, 'cleaning_type_id', type.id)}
                          disabled={!roomData.hasOrder}
                          tabIndex={-1}
                          title={`Opción ${index + 1}: ${type.name} (Presiona ${index + 1})`}
                          className={clsx(
                            'min-w-[75px] px-1.5 py-0.5 text-xs font-medium rounded transition-all',
                            {
                              'bg-green-500 text-white hover:bg-green-600': roomData.hasOrder && roomData.order?.cleaning_type_id === type.id,
                              'bg-gray-200 text-gray-700 hover:bg-gray-300': roomData.hasOrder && roomData.order?.cleaning_type_id !== type.id,
                              'bg-gray-100 text-gray-400 cursor-not-allowed': !roomData.hasOrder
                            }
                          )}
                        >
                          {type.name}
                        </button>
                      ))}
                    </div>
                  </td>

                  {/* Assigned Staff Dropdown */}
                  <td
                    className={clsx(
                      'px-2 py-1 bg-white transition-all',
                      {
                        'ring-4 ring-blue-400 ring-inset': focusedCell?.rowIndex === rowIndex && focusedCell?.columnIndex === EDITABLE_COLUMNS.STAFF
                      }
                    )}
                    data-row={rowIndex}
                    data-col={EDITABLE_COLUMNS.STAFF}
                    tabIndex={0}
                    onKeyDown={(e) => handleKeyDown(e, rowIndex, EDITABLE_COLUMNS.STAFF)}
                    onClick={(e) => {
                      // Only handle cell click if not clicking directly on the select
                      if (e.target.tagName !== 'SELECT') {
                        handleCellClick(rowIndex, EDITABLE_COLUMNS.STAFF);
                      }
                    }}
                  >
                    <select
                      value={roomData.order?.assigned_staff_id || ''}
                      onChange={(e) => handleFieldChange(room, 'assigned_staff_id', e.target.value || null)}
                      disabled={!roomData.hasOrder}
                      tabIndex={-1}
                      title={roomData.hasOrder ? 'Click para abrir o usa Enter/Espacio para ciclar (0-9 para selección rápida)' : ''}
                      className={clsx(
                        'w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 transition-all font-medium',
                        {
                          'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200': !roomData.hasOrder,
                          'bg-white border-red-500 focus:ring-red-500 focus:border-red-600': roomData.hasOrder && !roomData.order?.assigned_staff_id,
                          'bg-white border-gray-300 hover:border-blue-400 focus:ring-blue-500': roomData.hasOrder && roomData.order?.assigned_staff_id
                        }
                      )}
                    >
                      <option value="">Sin asignar</option>
                      {sortedCleaningStaff.map((staff, index) => (
                        <option key={staff.id} value={staff.id}>
                          {staff.name} ({index + 1})
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Checkout Status Toggle */}
                  <td className="px-2 py-1 text-center bg-white">
                    <div className="flex justify-center">
                      <button
                        onClick={() => {
                          if (roomData.hasOrder && roomData.order?.id && !roomData.order?.is_temp) {
                            onCheckoutStatusChange(`room-${room.id}`, roomData.order.id, !roomData.order?.checkout_status);
                          }
                        }}
                        disabled={!roomData.hasOrder || !roomData.order?.id || roomData.order?.is_temp}
                        className={clsx(
                          'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 shadow-sm',
                          {
                            'bg-blue-500 focus:ring-blue-500': roomData.order?.checkout_status && roomData.hasOrder && roomData.order?.id && !roomData.order?.is_temp,
                            'bg-gray-300 focus:ring-gray-400': !roomData.order?.checkout_status && roomData.hasOrder && roomData.order?.id && !roomData.order?.is_temp,
                            'bg-gray-200 cursor-not-allowed': !roomData.hasOrder || !roomData.order?.id || roomData.order?.is_temp
                          }
                        )}
                        title={!roomData.order?.id || roomData.order?.is_temp ? 'Guarda la orden primero' : roomData.order?.checkout_status ? 'Checkout realizado' : 'Checkout pendiente'}
                      >
                        <span
                          className={clsx(
                            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-md',
                            {
                              'translate-x-4': roomData.order?.checkout_status,
                              'translate-x-0.5': !roomData.order?.checkout_status
                            }
                          )}
                        />
                      </button>
                    </div>
                  </td>

                  {/* Cleaning Status */}
                  <td className="px-2 py-1 text-center bg-white">
                    <span className={clsx(
                      'inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full',
                      {
                        'bg-green-100 text-green-700 border border-green-200': roomData.order?.cleaning_complete,
                        'bg-amber-100 text-amber-700 border border-amber-200': roomData.hasOrder && !roomData.order?.cleaning_complete,
                        'bg-gray-100 text-gray-500 border border-gray-200': !roomData.hasOrder
                      }
                    )}>
                      {roomData.order?.cleaning_complete ? 'Completada' : roomData.hasOrder ? 'Pendiente' : 'Sin orden'}
                    </span>
                  </td>
                </tr>
              );
            })
            )}
          </tbody>
        </table>

        {/* Common Spaces Section */}
        {commonSpaces.length > 0 && (
          <>
            <div className="px-4 py-3 bg-gradient-to-r from-green-50 to-teal-50 border-y border-green-200 mt-4">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center space-x-2">
                <FiMapPin className="h-4 w-4 text-green-600" aria-hidden="true" />
                <span>Espacios Comunes</span>
              </h3>
            </div>

            <table className="w-full">
              <thead className="bg-gradient-to-r from-green-50 to-teal-50 border-b-2 border-green-200">
                <tr>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-800">
                    <div className="flex items-center space-x-1.5">
                      <FiMapPin className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
                      <span>Espacio</span>
                    </div>
                  </th>
                  <th className="px-2 py-1 text-center text-sm font-semibold text-gray-800">
                    <div className="flex items-center justify-center space-x-1">
                      <FiCheckSquare className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
                      <span>Limpieza</span>
                    </div>
                  </th>
                  <th className="px-2 py-1 text-center text-sm font-semibold text-gray-800">
                    <div className="flex items-center justify-center space-x-1">
                      <FiUser className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
                      <span>Personal</span>
                    </div>
                  </th>
                  <th className="px-2 py-1 text-center text-sm font-semibold text-gray-800">
                    <div className="flex items-center justify-center space-x-1">
                      <FiClock className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
                      <span>Estado</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedSpaces.map((space, spaceIndex) => {
                  const spaceData = spaceOrdersMap[space.id];
                  // Combined row index for keyboard navigation
                  const combinedRowIndex = flatRoomsList.length + spaceIndex;

                  return (
                    <tr key={`space-${space.id}`} className="hover:bg-green-50 border-b border-gray-200 transition-colors">
                      {/* Space Name */}
                      <td className="px-3 py-1 bg-white">
                        <div className="flex items-center space-x-1.5">
                          <div className="flex items-center justify-start text-sm font-medium text-gray-700">
                            {space.name}
                          </div>
                          <span className="text-xs text-gray-500">
                            ({space.cleaning_time_minutes} min)
                          </span>
                        </div>
                      </td>

                      {/* Has Order Checkbox */}
                      <td
                        className={clsx(
                          'px-2 py-1 text-center bg-white transition-all',
                          {
                            'ring-4 ring-green-400 ring-inset': focusedCell?.rowIndex === combinedRowIndex && focusedCell?.columnIndex === SPACE_EDITABLE_COLUMNS.HAS_ORDER
                          }
                        )}
                        data-row={combinedRowIndex}
                        data-col={SPACE_EDITABLE_COLUMNS.HAS_ORDER}
                        tabIndex={0}
                        onKeyDown={(e) => handleKeyDown(e, combinedRowIndex, SPACE_EDITABLE_COLUMNS.HAS_ORDER)}
                        onClick={() => handleCellClick(combinedRowIndex, SPACE_EDITABLE_COLUMNS.HAS_ORDER)}
                      >
                        <div className="flex justify-center">
                          <button
                            onClick={() => handleOrderToggle(space, !spaceData.hasOrder, 'space')}
                            tabIndex={-1}
                            className={clsx(
                              'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 shadow-sm',
                              {
                                'bg-green-500 focus:ring-green-500': spaceData.hasOrder,
                                'bg-gray-300 focus:ring-gray-400': !spaceData.hasOrder
                              }
                            )}
                          >
                            <span
                              className={clsx(
                                'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-md',
                                {
                                  'translate-x-4': spaceData.hasOrder,
                                  'translate-x-0.5': !spaceData.hasOrder
                                }
                              )}
                            />
                          </button>
                        </div>
                      </td>

                      {/* Assigned Staff Dropdown */}
                      <td
                        className={clsx(
                          'px-2 py-1 bg-white transition-all',
                          {
                            'ring-4 ring-green-400 ring-inset': focusedCell?.rowIndex === combinedRowIndex && focusedCell?.columnIndex === SPACE_EDITABLE_COLUMNS.STAFF
                          }
                        )}
                        data-row={combinedRowIndex}
                        data-col={SPACE_EDITABLE_COLUMNS.STAFF}
                        tabIndex={0}
                        onKeyDown={(e) => handleKeyDown(e, combinedRowIndex, SPACE_EDITABLE_COLUMNS.STAFF)}
                        onClick={(e) => {
                          // Only handle cell click if not clicking directly on the select
                          if (e.target.tagName !== 'SELECT') {
                            handleCellClick(combinedRowIndex, SPACE_EDITABLE_COLUMNS.STAFF);
                          }
                        }}
                      >
                        <select
                          value={spaceData.order?.assigned_staff_id || ''}
                          onChange={(e) => handleFieldChange(space, 'assigned_staff_id', e.target.value || null, 'space')}
                          disabled={!spaceData.hasOrder}
                          tabIndex={-1}
                          title={spaceData.hasOrder ? 'Click para abrir o usa Enter/Espacio para ciclar (0-9 para selección rápida)' : ''}
                          className={clsx(
                            'w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 transition-all font-medium',
                            {
                              'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200': !spaceData.hasOrder,
                              'bg-white border-red-500 focus:ring-red-500 focus:border-red-600': spaceData.hasOrder && !spaceData.order?.assigned_staff_id,
                              'bg-white border-gray-300 hover:border-green-400 focus:ring-green-500': spaceData.hasOrder && spaceData.order?.assigned_staff_id
                            }
                          )}
                        >
                          <option value="">Sin asignar</option>
                          {sortedCleaningStaff.map((staff, index) => (
                            <option key={staff.id} value={staff.id}>
                              {staff.name} ({index + 1})
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Cleaning Status */}
                      <td className="px-2 py-1 text-center bg-white">
                        <span className={clsx(
                          'inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full',
                          {
                            'bg-green-100 text-green-700 border border-green-200': spaceData.order?.cleaning_complete,
                            'bg-amber-100 text-amber-700 border border-amber-200': spaceData.hasOrder && !spaceData.order?.cleaning_complete,
                            'bg-gray-100 text-gray-500 border border-gray-200': !spaceData.hasOrder
                          }
                        )}>
                          {spaceData.order?.cleaning_complete ? 'Completada' : spaceData.hasOrder ? 'Pendiente' : 'Sin orden'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredAndSortedSpaces.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                No hay espacios comunes que mostrar con los filtros actuales
              </div>
            )}
          </>
        )}

        {filteredAndSortedRooms.length === 0 && commonSpaces.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No hay habitaciones ni espacios comunes que mostrar
          </div>
        )}

        {filteredAndSortedRooms.length === 0 && commonSpaces.length > 0 && filteredAndSortedSpaces.length > 0 && (
          <div className="p-8 text-center text-gray-500">
            No hay habitaciones que mostrar con los filtros actuales
          </div>
        )}
      </div>
    </div>

        {/* Staff Schedule Side Panel */}
        {showStaffSchedule && (
          <div className="w-1/6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 sticky top-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                <FiUsers className="h-4 w-4 text-blue-600" aria-hidden="true" />
                <span>Personal con órdenes asignadas</span>
              </h3>

              {loadingSchedules ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedCleaningStaff
                    .filter(staff => {
                      // Filter staff with assigned orders for this date
                      const hasAssignedOrders = Object.values(roomOrdersMap).some(
                        data => data.order?.assigned_staff_id === staff.id
                      ) || Object.values(spaceOrdersMap).some(
                        data => data.order?.assigned_staff_id === staff.id
                      );
                      return hasAssignedOrders;
                    })
                    .map(staff => {
                      // Get assigned rooms and spaces
                      const assignedRoomsList = Object.values(roomOrdersMap).filter(
                        data => data.order?.assigned_staff_id === staff.id
                      );
                      const assignedSpacesList = Object.values(spaceOrdersMap).filter(
                        data => data.order?.assigned_staff_id === staff.id
                      );

                      // Calculate estimated hours based on cleaning times with type modifiers
                      let estimatedHours = 0;
                      assignedRoomsList.forEach(data => {
                        if (data.room?.cleaning_time_minutes) {
                          // Get cleaning type modifier if assigned
                          const cleaningType = data.order?.cleaning_type_id 
                            ? cleaningTypes.find(t => t.id === data.order.cleaning_type_id)
                            : null;
                          const modifiedTime = calculateModifiedCleaningTime(
                            data.room.cleaning_time_minutes,
                            cleaningType?.time_modifier_type || 'none',
                            cleaningType?.time_modifier_value || 0
                          );
                          estimatedHours += modifiedTime / 60;
                        }
                      });
                      assignedSpacesList.forEach(data => {
                        if (data.space?.cleaning_time_minutes) {
                          // Common spaces use their base cleaning time directly
                          // (cleaning type modifiers don't apply to common spaces)
                          estimatedHours += data.space.cleaning_time_minutes / 60;
                        }
                      });

                      // Get scheduled hours for this staff on this date
                      const schedule = staffSchedules.find(s => s.employee_id === staff.id);
                      const scheduledHours = schedule?.hours || 0;

                      // Determine if workload is appropriate
                      const workloadStatus = scheduledHours === 0
                        ? 'no-schedule'
                        : estimatedHours > scheduledHours * 1.2
                        ? 'overloaded'
                        : estimatedHours < scheduledHours * 0.5
                        ? 'underutilized'
                        : 'adequate';

                      return (
                        <div
                          key={staff.id}
                          className={clsx(
                            'p-3 rounded-lg border-2 transition-all',
                            {
                              'bg-white border-green-300': workloadStatus === 'adequate',
                              'bg-red-50 border-red-300': workloadStatus === 'overloaded',
                              'bg-yellow-50 border-yellow-300': workloadStatus === 'underutilized',
                              'bg-gray-50 border-gray-300': workloadStatus === 'no-schedule'
                            }
                          )}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <FiUser className="h-4 w-4 text-blue-600" aria-hidden="true" />
                              <span className="font-semibold text-gray-900 text-sm">{staff.name}</span>
                            </div>

                            <div className="text-right">
                              <div className={clsx(
                                'text-sm font-bold',
                                {
                                  'text-green-700': workloadStatus === 'adequate',
                                  'text-red-700': workloadStatus === 'overloaded',
                                  'text-yellow-700': workloadStatus === 'underutilized',
                                  'text-gray-500': workloadStatus === 'no-schedule'
                                }
                              )}>
                                {scheduledHours > 0 ? `${scheduledHours}h` : 'Sin'}
                              </div>
                              {scheduledHours > 0 && (
                                <div className="text-[10px] text-gray-600">
                                  Est: {estimatedHours.toFixed(1)}h
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Assigned Rooms */}
                          {assignedRoomsList.length > 0 && (
                            <div className="mb-2">
                              <div className="flex items-center space-x-1 mb-1">
                                <FiHome className="h-3 w-3 text-blue-500" aria-hidden="true" />
                                <span className="text-xs font-medium text-gray-700">Habitaciones ({assignedRoomsList.length})</span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {assignedRoomsList.map(data => (
                                  <span
                                    key={data.room.id}
                                    className="inline-flex items-center px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded"
                                  >
                                    {data.room.number}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Assigned Common Spaces */}
                          {assignedSpacesList.length > 0 && (
                            <div className="mb-2">
                              <div className="flex items-center space-x-1 mb-1">
                                <FiMapPin className="h-3 w-3 text-green-500" aria-hidden="true" />
                                <span className="text-xs font-medium text-gray-700">Espacios ({assignedSpacesList.length})</span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {assignedSpacesList.map(data => (
                                  <span
                                    key={data.space.id}
                                    className="inline-flex items-center px-1.5 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded"
                                  >
                                    {data.space.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {schedule?.shift && (
                            <div className="pt-2 border-t border-gray-200">
                              <span className="text-xs text-gray-600">
                                <strong>Turno:</strong> {schedule.shift}
                              </span>
                            </div>
                          )}

                          {workloadStatus === 'overloaded' && (
                            <div className="pt-2 border-t border-red-200 mt-2">
                              <div className="flex items-start space-x-1 text-xs text-red-700">
                                <FiAlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                <span>Carga excesiva</span>
                              </div>
                            </div>
                          )}

                          {workloadStatus === 'underutilized' && (
                            <div className="pt-2 border-t border-yellow-200 mt-2">
                              <div className="flex items-start space-x-1 text-xs text-yellow-700">
                                <FiAlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                <span>Carga baja</span>
                              </div>
                            </div>
                          )}

                          {workloadStatus === 'no-schedule' && (
                            <div className="pt-2 border-t border-gray-200 mt-2">
                              <div className="flex items-start space-x-1 text-xs text-gray-600">
                                <FiAlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                <span>Sin cuadrante</span>
                              </div>
                            </div>
                          )}

                          {/* Botón para aplicar cuadrante estimado */}
                          {estimatedHours > 0 && (
                            <div className="pt-2 mt-2 border-t border-gray-200">
                              <button
                                onClick={() => applyEstimatedSchedule(staff.id, estimatedHours, schedule)}
                                disabled={savingScheduleForStaff === staff.id}
                                className={clsx(
                                  'w-full flex items-center justify-center space-x-1.5 px-2 py-1.5 rounded text-xs font-medium transition-colors',
                                  {
                                    'bg-blue-600 hover:bg-blue-700 text-white': savingScheduleForStaff !== staff.id,
                                    'bg-blue-400 text-white cursor-not-allowed': savingScheduleForStaff === staff.id
                                  }
                                )}
                              >
                                {savingScheduleForStaff === staff.id ? (
                                  <>
                                    <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Guardando...</span>
                                  </>
                                ) : (
                                  <>
                                    <FiCalendar className="h-3 w-3" aria-hidden="true" />
                                    <span>Aplicar cuadrante ({estimatedHours.toFixed(1)}h)</span>
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}

                  {sortedCleaningStaff.filter(staff => {
                    const hasAssignedOrders = Object.values(roomOrdersMap).some(
                      data => data.order?.assigned_staff_id === staff.id
                    ) || Object.values(spaceOrdersMap).some(
                      data => data.order?.assigned_staff_id === staff.id
                    );
                    return hasAssignedOrders;
                  }).length === 0 && (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      No hay personal con órdenes asignadas
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirmar acción
            </h3>
            <p className="text-gray-700 mb-6">
              La orden de <span className="font-semibold">{confirmDialog.itemName}</span> tiene personal asignado (<span className="font-semibold">{confirmDialog.staffName}</span>).
              ¿Está seguro de que desea desmarcar esta orden?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelUncheckOrder}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmUncheckOrder}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Sí, desmarcar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Save Button */}
      <div className={clsx(
        'fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 transition-all duration-300',
        {
          'opacity-100 translate-y-0': hasChanges,
          'opacity-0 translate-y-4 pointer-events-none': !hasChanges
        }
      )}>
        <button
          onClick={onSave}
          disabled={isSaving}
          className="flex items-center space-x-3 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full shadow-2xl transition-all hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {isSaving ? (
            <>
              <LoadingSpinner size="sm" color="white" />
              <span>Guardando...</span>
            </>
          ) : (
            <>
              <FiSave className="h-5 w-5" aria-hidden="true" />
              <span>Guardar Cambios</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CleaningOrdersTable;